// import components
import { Box, CircularProgress, List, Typography } from '@mui/material';
import PrescriptionCard from './PrescriptionCard';

// import external libraries
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import type { PrescriptionResponse } from 'generated/index';
import { DateNavigation } from 'components/shared/index';
import { usePrescriptionDateInterval } from './PatientPrescriptions.hooks';

interface PatientPrescriptionsProps {
  prescriptions?: PrescriptionResponse[];
  isLoading: boolean;
}

const PatientPrescriptions = ({
  prescriptions,
  isLoading
}: PatientPrescriptionsProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'careReceiversHome.prescriptions'
  });

  const { selectedDate, dateInterval, onDateChange } =
    usePrescriptionDateInterval();

  return (
    <Box p={3.5}>
      {isLoading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (!prescriptions || prescriptions.length === 0) && (
        <Typography variant="body2" color="text.secondary">
          {t('noData')}
        </Typography>
      )}

      {!isLoading && prescriptions && prescriptions.length > 0 && (
        <>
          <Box mb={4}>
            <DateNavigation
              defaultPeriod="eightWeeks"
              defaultDate={selectedDate}
              onDateChange={onDateChange}
            />
          </Box>
          <List
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}
          >
            {prescriptions.map((prescription) => (
              <PrescriptionCard
                key={prescription.id}
                prescription={prescription}
                dateInterval={dateInterval}
              />
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default memo(PatientPrescriptions);
